import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { COMMON_REGISTER_QUERY_KEY } from '@/modules/common/queries/registers.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { CURRENT_ADMIN_ISSUERS_QUERY_KEY } from '@/modules/admin-dashboard/queries/issuers.tsx';
import { CURRENT_USER_REGISTERS_QUERY_KEY } from '@/modules/investor-dashboard/queries/registers.tsx';
import { REGISTER_BANKS_LIST_QUERY_KEY } from '@/modules/issuer-dashboard/queries/banks.ts';
import { NOTIFICATIONS_LIST_QUERY_KEY } from '@/modules/issuer-dashboard/queries/event-logs.ts';
import {
  ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY,
  ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY,
  ISSUER_PORTFOLIO_TRANSACTIONS_LIST_QUERY_KEY,
  ISSUER_PORTFOLIO_TRANSACTION_BY_ID_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import {
  CreateRegisterRequest,
  CreateRegisterResponse,
  CreateUserRegisterRequest,
  CreateUserRegisterResponse,
  DeleteRegisterResponse,
  GetRegisterListResponse,
  GetRegistersListV1SrRegistersGetParams,
  UpdateRegisterRequest,
  UpdateRegisterResponse,
} from 'api';

export const CURRENT_ADMIN_REGISTERS_QUERY_KEY = 'vestra/admin-register-list';

export const useAdminRegisterListQuery = (
  payload: GetRegistersListV1SrRegistersGetParams,
  options?: { disabled?: boolean; cacheKey?: string },
) => {
  const { page, ...data } = payload;
  const { disabled = false, cacheKey } = options || {};

  return useInfiniteQuery<GetRegisterListResponse>({
    queryKey: [CURRENT_ADMIN_REGISTERS_QUERY_KEY, JSON.stringify(data), cacheKey],
    staleTime: 1000 * 60 * 60, // 1 hour
    queryFn: async ({ pageParam }) => {
      const response = await api.v1.getRegistersListV1SrRegistersGet({
        ...payload,
        page: pageParam?.page || page,
        search: pageParam?.search || payload.search,
      });

      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    refetchInterval: 1000 * 60 * 5, //5min
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return { page: page + 1 };
      }
    },
    enabled: !disabled,
  });
};

export const useAdminAddRegisterMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: CreateRegisterRequest) => {
      const response = await withErrorHandling<CreateRegisterResponse>(
        api.v1.createRegisterV1SrRegistersPost(payload),
        {
          loading: 'Creating register...',
          success: 'Register created',
          setErrors,
          errorDuration: 10000,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([REGISTER_BANKS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_USER_REGISTERS_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ADMIN_REGISTERS_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ADMIN_ISSUERS_QUERY_KEY]);
      await queryClient.invalidateQueries([COMMON_REGISTER_QUERY_KEY]);
      await queryClient.invalidateQueries([NOTIFICATIONS_LIST_QUERY_KEY]);
    },
  });
};

export const useAdminUpdateRegisterMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: UpdateRegisterRequest) => {
      const response = await withErrorHandling<UpdateRegisterResponse>(
        api.v1.updateRegisterV1SrRegistersPut(payload),
        {
          loading: 'Updating register...',
          success: 'Register updated',
          setErrors,
          errorDuration: 10000,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([REGISTER_BANKS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_USER_REGISTERS_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ADMIN_REGISTERS_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ADMIN_ISSUERS_QUERY_KEY]);
      await queryClient.invalidateQueries([COMMON_REGISTER_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSETS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_ASSET_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTIONS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_PORTFOLIO_TRANSACTION_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([NOTIFICATIONS_LIST_QUERY_KEY]);
    },
  });
};

export interface DeleteRegisterRequest {
  id: number;
}

export const useAdminDeleteUserRegisterMutation = ({
  onSuccess,
}: { onSuccess?: () => void } = {}) => {
  return useMutation({
    mutationFn: async (payload: DeleteRegisterRequest) => {
      const response = await withErrorHandling<DeleteRegisterResponse>(
        api.v1.deleteRegisterV1SrRegistersIdDelete({ id: payload.id }),
        {
          loading: 'Deleting register...',
          success: 'Register deleted',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([CURRENT_ADMIN_ISSUERS_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ADMIN_REGISTERS_QUERY_KEY]);
      onSuccess && onSuccess();
    },
  });
};

export const useAdminCreateUserRegisterMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: CreateUserRegisterRequest) => {
      const response = await withErrorHandling<CreateUserRegisterResponse>(
        api.v1.createUserRegisterV1SrUserRegistersPost(payload),
        {
          loading: 'Applying register...',
          success: 'Register applied',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_ADMIN_ISSUERS_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_ADMIN_REGISTERS_QUERY_KEY]);
    },
  });
};
